import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Plus, CalendarCheck, Clock, VideoCamera, Trash, PencilSimple, User } from "@phosphor-icons/react";
import PageHeader from "@/components/PageHeader";
import ConfirmDelete from "@/components/ConfirmDelete";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PLATFORMS = ["Google Meet", "Microsoft Teams", "Zoom", "Cisco WebEx", "Altro"];

function formatDateIT(iso) {
  if (!iso) return "";
  const p = iso.split("-");
  if (p.length === 3 && p[0].length === 4) return `${p[2]}/${p[1]}/${p[0]}`;
  return iso;
}

const EMPTY = { candidate_id: "", title: "", date: "", time: "10:00", duration: 60, platform: "Google Meet", meeting_link: "", notes: "" };

export default function InterviewsPage() {
  const [interviews, setInterviews] = useState([]);
  const [candidates, setCandidates] = useState([]);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ ...EMPTY });
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  });

  const load = () => {
    axios.get(`${API}/interviews`, { withCredentials: true }).then(r => setInterviews(r.data)).catch(() => {});
    axios.get(`${API}/candidates`, { withCredentials: true }).then(r => setCandidates(r.data.filter(c => c.status !== "Archiviato"))).catch(() => {});
  };
  const { activeWorkspaceId } = useAuth();
  // Batch 62.A: re-load on workspace switch for agency accounts
  useEffect(() => { load(); }, [activeWorkspaceId]); // eslint-disable-line react-hooks/exhaustive-deps

  const up = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const save = async () => {
    if (!form.candidate_id || !form.date) { toast.error("Seleziona candidato e data"); return; }
    try {
      if (editId) {
        const { data } = await axios.put(`${API}/interviews/${editId}`, form, { withCredentials: true });
        if (data.email_sent) toast.success("Email di aggiornamento inviata al candidato");
      } else {
        const { data } = await axios.post(`${API}/interviews`, form, { withCredentials: true });
        if (data.email_sent) toast.success("Email con dettagli colloquio inviata al candidato");
      }
      toast.success(editId ? "Colloquio aggiornato" : "Colloquio programmato");
      setOpen(false); setForm({ ...EMPTY }); setEditId(null); load();
    } catch { toast.error("Errore"); }
  };

  const deleteInterview = async (id) => {
    try { await axios.delete(`${API}/interviews/${id}`, { withCredentials: true }); toast.success("Colloquio eliminato"); load(); } catch { toast.error("Errore"); }
  };

  // Calendar logic
  const [year, month] = selectedMonth.split("-").map(Number);
  const firstDay = new Date(year, month - 1, 1).getDay();
  const daysInMonth = new Date(year, month, 0).getDate();
  const offset = firstDay === 0 ? 6 : firstDay - 1;
  const today = new Date().toISOString().split("T")[0];

  const interviewsByDate = {};
  interviews.forEach(i => {
    if (!interviewsByDate[i.date]) interviewsByDate[i.date] = [];
    interviewsByDate[i.date].push(i);
  });

  const prevMonth = () => {
    const d = new Date(year, month - 2, 1);
    setSelectedMonth(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
  };
  const nextMonth = () => {
    const d = new Date(year, month, 1);
    setSelectedMonth(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
  };

  const MONTH_NAMES = ["Gennaio","Febbraio","Marzo","Aprile","Maggio","Giugno","Luglio","Agosto","Settembre","Ottobre","Novembre","Dicembre"];
  const DAY_NAMES = ["Lun","Mar","Mer","Gio","Ven","Sab","Dom"];

  const openNewForDate = (dateStr) => {
    setForm({ ...EMPTY, date: dateStr });
    setEditId(null);
    setOpen(true);
  };

  return (
    <div className="animate-fade-in" data-testid="interviews-page">
      <PageHeader title="Colloqui" subtitle={`${interviews.length} colloqui programmati`}>
        <Button className="bg-[#1B4FD8] hover:bg-[#0D1F3C]" onClick={() => { setForm({ ...EMPTY }); setEditId(null); setOpen(true); }} data-testid="new-interview-btn">
          <Plus size={18} className="mr-2" />Programma Colloquio
        </Button>
      </PageHeader>

      <div className="grid grid-cols-3 gap-6 mt-6">
        {/* Calendar */}
        <div className="col-span-2">
          <Card className="border-[#0D1F3C]/8">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <Button variant="ghost" size="sm" onClick={prevMonth}>&larr;</Button>
                <CardTitle className="text-base font-medium text-[#0D1F3C]">{MONTH_NAMES[month - 1]} {year}</CardTitle>
                <Button variant="ghost" size="sm" onClick={nextMonth}>&rarr;</Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-7 gap-px">
                {DAY_NAMES.map(d => (
                  <div key={d} className="text-[10px] text-center font-medium text-[#0D1F3C]/40 uppercase pb-2">{d}</div>
                ))}
                {Array.from({ length: offset }).map((_, i) => <div key={`e-${i}`} />)}
                {Array.from({ length: daysInMonth }).map((_, i) => {
                  const day = i + 1;
                  const dateStr = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                  const dayInterviews = interviewsByDate[dateStr] || [];
                  const isToday = dateStr === today;
                  return (
                    <div key={day} onClick={() => openNewForDate(dateStr)}
                      className={`min-h-[130px] p-1.5 border border-[#0D1F3C]/5 rounded-lg cursor-pointer transition-colors hover:bg-[#F4F7FB]
                        ${isToday ? 'bg-[#1B4FD8]/[0.03] border-[#1B4FD8]/20' : ''}`}
                      data-testid={`cal-day-${dateStr}`}>
                      <span className={`text-xs font-medium ${isToday ? 'text-[#1B4FD8]' : 'text-[#0D1F3C]/60'}`}>{day}</span>
                      {dayInterviews.map(iv => (
                        <div key={iv.id} className="mt-0.5 px-1.5 py-0.5 rounded bg-[#1B4FD8]/10 text-[9px] text-[#1B4FD8] truncate cursor-pointer"
                          onClick={(e) => { e.stopPropagation(); setForm({ ...EMPTY, ...iv }); setEditId(iv.id); setOpen(true); }}>
                          {iv.time} {iv.candidate_name}
                        </div>
                      ))}
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Upcoming list */}
        <div>
          <Card className="border-[#0D1F3C]/8">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-[#0D1F3C]">Prossimi Colloqui</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {interviews.filter(i => i.date >= today).length === 0 ? (
                <p className="text-xs text-[#0D1F3C]/40 text-center py-6">Nessun colloquio in programma</p>
              ) : interviews.filter(i => i.date >= today).slice(0, 10).map(iv => (
                <div key={iv.id} className="p-3 rounded-lg border border-[#0D1F3C]/8 hover:border-[#1B4FD8]/20 transition-colors">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-medium text-[#0D1F3C]">{iv.candidate_name}</span>
                    <div className="flex items-center gap-1">
                      <Button variant="ghost" size="sm" className="h-6 w-6 p-0" title="Modifica"
                        onClick={() => { setForm({ ...EMPTY, ...iv }); setEditId(iv.id); setOpen(true); }}>
                        <PencilSimple size={12} />
                      </Button>
                      <ConfirmDelete title="Elimina colloquio" description="Eliminare questo colloquio?" onConfirm={() => deleteInterview(iv.id)}>
                        <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-red-400" title="Elimina"><Trash size={12} /></Button>
                      </ConfirmDelete>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 text-[10px] text-[#0D1F3C]/50">
                    <CalendarCheck size={12} />{formatDateIT(iv.date)}
                    <Clock size={12} />{iv.time}
                    <VideoCamera size={12} />{iv.platform}
                  </div>
                  <p className="text-[10px] text-[#0D1F3C]/40 mt-1">{iv.position_name}</p>
                  {iv.meeting_link && (
                    <a href={iv.meeting_link} target="_blank" rel="noopener noreferrer"
                      className="text-[10px] text-[#1B4FD8] hover:underline mt-1 block truncate">Apri link</a>
                  )}
                </div>
              ))}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* New/Edit Dialog */}
      <Dialog open={open} onOpenChange={o => { setOpen(o); if (!o) { setForm({ ...EMPTY }); setEditId(null); }}}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{editId ? "Modifica Colloquio" : "Programma Colloquio"}</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-3">
            <div>
              <Label className="text-xs text-[#0D1F3C]/60 mb-1 block">Candidato *</Label>
              <Select value={form.candidate_id} onValueChange={v => {
                up("candidate_id", v);
                const c = candidates.find(x => x.id === v);
                if (c && !form.title) up("title", `Colloquio ${c.first_name} ${c.last_name}`);
              }}>
                <SelectTrigger data-testid="iv-candidate"><SelectValue placeholder="Seleziona candidato" /></SelectTrigger>
                <SelectContent>
                  {candidates.map(c => (
                    <SelectItem key={c.id} value={c.id}>
                      <div className="flex items-center gap-2">
                        <User size={14} className="text-[#0D1F3C]/40" />
                        <span>{c.first_name} {c.last_name}</span>
                        <span className="text-[#0D1F3C]/30 text-xs">({c.email})</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs text-[#0D1F3C]/60 mb-1 block">Titolo</Label>
              <Input value={form.title} onChange={e => up("title", e.target.value)} placeholder="es. Primo colloquio conoscitivo" /></div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label className="text-xs text-[#0D1F3C]/60 mb-1 block">Data *</Label>
                <Input type="date" value={form.date} onChange={e => up("date", e.target.value)} data-testid="iv-date" /></div>
              <div><Label className="text-xs text-[#0D1F3C]/60 mb-1 block">Ora</Label>
                <Input type="time" value={form.time} onChange={e => up("time", e.target.value)} /></div>
              <div><Label className="text-xs text-[#0D1F3C]/60 mb-1 block">Durata (min)</Label>
                <Input type="number" value={form.duration} onChange={e => up("duration", parseInt(e.target.value) || 60)} /></div>
            </div>
            <div>
              <Label className="text-xs text-[#0D1F3C]/60 mb-1 block">Piattaforma</Label>
              <Select value={form.platform} onValueChange={v => up("platform", v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{PLATFORMS.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs text-[#0D1F3C]/60 mb-1 block">Link Meeting</Label>
              <Input value={form.meeting_link} onChange={e => up("meeting_link", e.target.value)} placeholder="https://meet.google.com/..." /></div>
            <div><Label className="text-xs text-[#0D1F3C]/60 mb-1 block">Note</Label>
              <Textarea value={form.notes} onChange={e => up("notes", e.target.value)} rows={2} placeholder="Note sul colloquio..." /></div>
            <Button onClick={save} className="w-full bg-[#1B4FD8] hover:bg-[#0D1F3C]" data-testid="iv-save">{editId ? "Aggiorna" : "Programma"}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
